import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Header from "./common/Header/Header";
import {Switch, Route, Redirect, useLocation} from "react-router-dom";
import MainPage from "./pages/MainPage/MainPage";
import AboutUs from "./pages/AboutUs/AboutUs";
import OurServices from "./pages/OurServices/OurServices";
import Contact from "./pages/Contact/Contact";
import Footer from "./common/Footer/Footer";
import WhatsAppButton from "./common/WhatsAppButton/WhatsAppButton";
import AgreementGate from "./pages/Auction/AgreementGate";
import Login from "./pages/Login/Login";
import Market from "./pages/Market/Market";
import CarDetails from "./pages/CarDetails/CarDetails";
import AdminCars from "./pages/Admin/Cars/AdminCars";
import AdminLeads from "./pages/Admin/Leads/AdminLeads";
import AuctionAuth from "./pages/Auction/AuctionAuth";
import AuctionAccount from "./pages/Auction/AuctionAccount";
import AuctionList from "./pages/Auction/AuctionList";
import AuctionLanding from "./pages/Auction/AuctionLanding";
import AuctionEvent from "./pages/Auction/AuctionEvent";
import AuctionLot from "./pages/Auction/AuctionLot";
import AuctionLive from "./pages/Auction/AuctionLive";
import AuctionMy from "./pages/Auction/AuctionMy";
import AuctionWon from "./pages/Auction/AuctionWon";
import AuctionBids from "./pages/Auction/AuctionBids";
import AuctionWatchlist from "./pages/Auction/AuctionWatchlist";
import AuctionInvoice from "./pages/Auction/AuctionInvoice";
import AuctionDepositInvoice from "./pages/Auction/AuctionDepositInvoice";
import AdminAuctionEvents from "./pages/Admin/Auction/AdminAuctionEvents";
import AdminAuctionConsole from "./pages/Admin/Auction/AdminAuctionConsole";
import AdminAuctionKyc from "./pages/Admin/Auction/AdminAuctionKyc";
import AdminAuctionDeposits from "./pages/Admin/Auction/AdminAuctionDeposits";
import AdminAuctionBidders from "./pages/Admin/Auction/AdminAuctionBidders";
import AdminAuctionWon from "./pages/Admin/Auction/AdminAuctionWon";
import axios from "axios";
import { usersActions } from "./data/user";
import { buildLangUrl, langForCountry } from "./common/langRouting";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function App() {
    const user = useSelector((state) => state.user);
    const dispatch = useDispatch();
    const location = useLocation();

    // First-visit language auto-detection by country. Only runs when the visitor
    // is on an un-prefixed (Georgian) URL and has not already chosen/been detected
    // — a prefixed URL or a prior choice locks the language. Detection redirects to
    // the prefixed URL (e.g. Armenia -> /am/...), which reloads with that language.
    useEffect(() => {
        try {
            const path = window.location.pathname;
            const hasPrefix = /^\/(en|ru|am)(\/|$)/.test(path);
            const locked = localStorage.getItem("langLocked") === "1";
            if (!hasPrefix && !locked) {
                const decide = (code) => {
                    try { localStorage.setItem("langLocked", "1"); } catch (e) { /* ignore */ }
                    if (code && code !== "geo") {
                        try { localStorage.setItem("language", code); } catch (e) { /* ignore */ }
                        window.location.replace(buildLangUrl(code));
                    }
                };
                // A .am domain is a strong signal; otherwise look up the country by IP.
                if (window.location.host.includes(".am")) {
                    decide("am");
                } else {
                    fetch("https://get.geojs.io/v1/ip/country.json")
                        .then((r) => r.json())
                        .then((d) => decide(langForCountry(d && d.country) || "geo"))
                        .catch(() => { try { localStorage.setItem("langLocked", "1"); } catch (e) { /* ignore */ } });
                }
            }
        } catch (e) { /* detection is best-effort */ }
    }, []);

    useEffect(() => {
        axios('/api/user')
            .then((res) => {
                const data = res.data.data;
                dispatch(usersActions.changeIsLoggedIn(true));
                // The payload is the user record itself — wrapping it in {data}
                // meant every field landed as undefined in the store.
                dispatch(usersActions.saveUserData(data));
            })
            .catch(() => {
                dispatch(usersActions.changeIsLoggedIn(false));
            })
            .finally(() => {
                dispatch(usersActions.setAuthChecked(true));
            })
    }, [dispatch])

    // The admin panel brings its own chrome, so the marketing footer is hidden there.
    const isAdminRoute = location.pathname.startsWith("/admin");

    return (
        <main className="main-wrapper">
            <Header />
            <AgreementGate />
            <Switch>
                <Route exact path="/"><MainPage /></Route>
                <Route exact path="/about-us"><AboutUs /></Route>
                <Route exact path="/our-services"><OurServices /></Route>
                <Route exact path="/contact"><Contact /></Route>
                <Route exact path="/market"><Market /></Route>
                {!user.isLoggedIn && <Route exact path="/login"><Login /></Route>}
                <Route exact path="/car/:id"><CarDetails /></Route>
                <Route exact path="/auction"><AuctionLanding /></Route>
                <Route exact path="/auctions"><AuctionList /></Route>
                <Route exact path="/auction/register"><AuctionAuth mode="register" /></Route>
                <Route exact path="/auction/login"><AuctionAuth mode="login" /></Route>
                <Route exact path="/auction/account"><AuctionAccount /></Route>
                <Route exact path="/auction/my"><AuctionMy /></Route>
                <Route exact path="/auction/won"><AuctionWon /></Route>
                <Route exact path="/auction/bids"><AuctionBids /></Route>
                <Route exact path="/auction/watchlist"><AuctionWatchlist /></Route>
                <Route exact path="/auction/invoice/:id"><AuctionInvoice /></Route>
                <Route exact path="/auction/deposit-invoice"><AuctionDepositInvoice /></Route>
                <Route exact path="/auction/event/:id/live"><AuctionLive /></Route>
                <Route exact path="/auction/event/:id"><AuctionEvent /></Route>
                <Route exact path="/auction/lot/:id"><AuctionLot /></Route>
                <Route exact path="/admin"><Redirect to="/admin/cars" /></Route>
                <Route exact path="/admin/cars"><AdminCars /></Route>
                <Route exact path="/admin/leads"><AdminLeads /></Route>
                <Route exact path="/admin/auction/events"><AdminAuctionEvents /></Route>
                <Route exact path="/admin/auction/events/:id"><AdminAuctionConsole /></Route>
                <Route exact path="/admin/auction/kyc"><AdminAuctionKyc /></Route>
                <Route exact path="/admin/auction/deposits"><AdminAuctionDeposits /></Route>
                <Route exact path="/admin/auction/bidders"><AdminAuctionBidders /></Route>
                <Route exact path="/admin/auction/won"><AdminAuctionWon /></Route>
            </Switch>
            {!isAdminRoute && <Footer />}
            {!isAdminRoute && <WhatsAppButton />}
            <ToastContainer
                position="bottom-left"
                autoClose={4500}
                newestOnTop
                closeOnClick
                pauseOnFocusLoss
                draggable
                theme="dark"
                limit={3}
            />
        </main>
    );
}

export default App;
